import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';

const NotFound = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleContactUs = () => {
    navigate('/#contact');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* 404 Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            {/* 404 Number */}
            <div className="mb-8">
              <h1 className="text-8xl md:text-9xl font-bold text-[#00d690] opacity-20 mb-4">
                404
              </h1>
              <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            </div>

            {/* Main Content */}
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Oops! Page Not Found
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              The page you're looking for seems to have wandered off into the digital wilderness. 
              Don't worry, we'll help you find your way back!
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={handleGoHome}
                className="bg-[#00d690] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#00b876] transition-all duration-300 flex items-center justify-center gap-2 group"
              >
                <Icon name="Home" size={20} />
                Back to Home
                <Icon name="ArrowRight" size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>

              <button
                onClick={handleGoBack}
                className="border-2 border-[#00d690] text-[#00d690] px-8 py-4 rounded-lg font-semibold hover:bg-[#00d690] hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Icon name="ArrowLeft" size={20} />
                Go Back
              </button>
            </div>

            {/* Help Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Need Help?
              </h3>
              <p className="text-gray-600 mb-6">
                If you're looking for something specific, we're here to help! 
                Contact our team and we'll guide you to the right place.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 rounded-lg bg-gray-50 hover:bg-[#00d690]/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-[#00d690] rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name="Search" size={24} className="text-white" />
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Search Our Site</h4>
                  <p className="text-sm text-gray-600">Use the navigation menu to find what you need</p>
                </div>

                <div className="text-center p-4 rounded-lg bg-gray-50 hover:bg-[#00d690]/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-[#00d690] rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name="Mail" size={24} className="text-white" />
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Contact Us</h4>
                  <p className="text-sm text-gray-600">Get in touch with our support team</p>
                </div>

                <div className="text-center p-4 rounded-lg bg-gray-50 hover:bg-[#00d690]/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-[#00d690] rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name="Globe" size={24} className="text-white" />
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Browse Services</h4>
                  <p className="text-sm text-gray-600">Explore our web development services</p>
                </div>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={handleContactUs}
                  className="bg-gradient-to-r from-[#00d690] to-[#00b876] text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
                >
                  <Icon name="MessageCircle" size={20} />
                  Get in Touch
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-800 text-center mb-8">
              Popular Pages
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <button
                onClick={() => navigate('/')}
                className="p-6 bg-gray-50 rounded-lg hover:bg-[#00d690]/10 transition-all duration-300 text-left group"
              >
                <div className="w-10 h-10 bg-[#00d690] rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="Home" size={20} className="text-white" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Home</h4>
                <p className="text-sm text-gray-600">Back to our main page</p>
              </button>

              <button
                onClick={() => navigate('/services')}
                className="p-6 bg-gray-50 rounded-lg hover:bg-[#00d690]/10 transition-all duration-300 text-left group"
              >
                <div className="w-10 h-10 bg-[#00d690] rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="Settings" size={20} className="text-white" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Services</h4>
                <p className="text-sm text-gray-600">View our development services</p>
              </button>

              <button
                onClick={() => navigate('/#about')}
                className="p-6 bg-gray-50 rounded-lg hover:bg-[#00d690]/10 transition-all duration-300 text-left group"
              >
                <div className="w-10 h-10 bg-[#00d690] rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="Users" size={20} className="text-white" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">About Us</h4>
                <p className="text-sm text-gray-600">Learn more about OctalChip</p>
              </button>

              <button
                onClick={() => navigate('/#contact')}
                className="p-6 bg-gray-50 rounded-lg hover:bg-[#00d690]/10 transition-all duration-300 text-left group"
              >
                <div className="w-10 h-10 bg-[#00d690] rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="Mail" size={20} className="text-white" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Contact</h4>
                <p className="text-sm text-gray-600">Get in touch with us</p>
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NotFound;
